// V160.14.2: Request Deduplication System
// CURSOR: Create this file with full deduplication logic

import { getOptimizedImageUrl, CloudinaryOptions } from './cloudinaryOptimizer';

// =============================================================================
// GLOBAL STATE (Module-level singletons)
// =============================================================================

// L1 Memory cache: Map<cacheKey, optimizedUrl>
const memoryCache = new Map<string, string>();

// Active request tracking: Map<cacheKey, Promise>
// Prevents duplicate network requests for same resource
const pendingRequests = new Map<string, Promise<string>>();

// Diagnostic tracking: Map<cacheKey, count>
// Counts how many times same resource was requested simultaneously
const requestCounts = new Map<string, number>();

// Cache metadata for validation
interface CacheMetadata {
  timestamp: number;
  version: string;
}

const CACHE_VERSION = 'v160.14';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Local metadata store
const cacheMetadata = new Map<string, CacheMetadata>();

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * V206.21: Update cache directly with new URL (for signup/upload)
 *
 * @param userId - User ID
 * @param photoUrl - New photo URL from Cloudinary
 */
export function updateCachedImageUrl(userId: string, photoUrl: string): void {
  if (!userId || !photoUrl) {
    console.error('❌ V206.21: Invalid params for cache update', { userId, photoUrl });
    return;
  }

  const urlPart = photoUrl.split('/').pop() || photoUrl;
  // V221.1: Include Cloudinary version in cache key so overwrites show immediately
  // Cloudinary overwrite produces new URLs with /v<version>/ even though public_id stays the same.
  const versionMatch = photoUrl.match(/\/v(\d+)\//);
  const versionKey = versionMatch ? `-v${versionMatch[1]}` : '';
  const cacheKey = `photo-${userId}-${urlPart}${versionKey}`;

  // Update memory cache
  memoryCache.set(cacheKey, photoUrl);

  // Update metadata
  cacheMetadata.set(cacheKey, {
    timestamp: Date.now(),
    version: CACHE_VERSION
  });

  console.log('✅ V206.21: Cache updated directly', { userId, cacheKey });
}

/**
 * Get cached image URL with deduplication and optimization
 *
 * @param originalUrl - Raw Cloudinary URL from database
 * @param userId - User ID for cache key namespacing
 * @param options - Cloudinary optimization options
 * @returns Promise<optimized WebP URL>
 *
 * Deduplication: If multiple components request same image simultaneously,
 * only ONE network request is made. Other calls wait for same promise.
 */
// Overloads for backward compatibility
export function getCachedImageUrl(originalUrl: string): Promise<string>;
export function getCachedImageUrl(
  originalUrl: string,
  userId: string,
  options?: CloudinaryOptions
): Promise<string>;
export async function getCachedImageUrl(
  originalUrl: string,
  userId?: string,
  options: CloudinaryOptions = {}
): Promise<string> {
  // Validate input
  if (!originalUrl) {
    // eslint-disable-next-line no-console
    console.error('❌ V160.14: Invalid params', { originalUrl, userId });
    return originalUrl;
  }

  // Legacy path: single-arg usage – namespace under 'legacy'
  if (!userId) {
    userId = 'legacy';
  }

  if (!userId) {
    // eslint-disable-next-line no-console
    console.error('❌ V160.14: Invalid params', { originalUrl, userId });
    return originalUrl;
  }

  // Build user-specific cache key
  // V220: Include size in cache key for responsive images (mobile vs desktop)
  const urlPart = originalUrl.split('/').pop() || originalUrl;
  // V221.1: Include Cloudinary version in cache key so updated uploads don't reuse stale cached URLs
  const versionMatch = originalUrl.match(/\/v(\d+)\//);
  const versionKey = versionMatch ? `-v${versionMatch[1]}` : '';
  const sizeKey = options.width && options.height ? `-${options.width}x${options.height}` : '';
  const cacheKey = `photo-${userId}-${urlPart}${versionKey}${sizeKey}`;

  // ---------------------------------------------------------------------------
  // DEDUPLICATION CHECK: Is request already in flight?
  // ---------------------------------------------------------------------------
  if (pendingRequests.has(cacheKey)) {
    const count = (requestCounts.get(cacheKey) || 1) + 1;
    requestCounts.set(cacheKey, count);
    // eslint-disable-next-line no-console
    console.warn(`⚠️ V160.14: Deduped request #${count} for ${cacheKey}`);
    return pendingRequests.get(cacheKey)!;
  }

  // ---------------------------------------------------------------------------
  // MEMORY CACHE CHECK: Already loaded?
  // ---------------------------------------------------------------------------
  if (memoryCache.has(cacheKey)) {
    const cachedUrl = memoryCache.get(cacheKey)!;
    if (isCacheValid(cacheKey)) {
      return cachedUrl;
    } else {
      // Cache expired, remove it
      memoryCache.delete(cacheKey);
    }
  }

  // ---------------------------------------------------------------------------
  // CACHE MISS: Load and cache image
  // ---------------------------------------------------------------------------
  requestCounts.set(cacheKey, 1);
  const requestPromise = loadAndCacheImage(originalUrl, cacheKey, options);
  pendingRequests.set(cacheKey, requestPromise);

  try {
    const result = await requestPromise;
    return result;
  } finally {
    pendingRequests.delete(cacheKey);
    const count = requestCounts.get(cacheKey) || 0;
    if (count > 1) {
      // eslint-disable-next-line no-console
      console.warn(`📊 V160.14: Prevented ${count - 1} duplicate requests for ${cacheKey}`);
    }
  }
}

/**
 * Clear all caches (useful for testing or manual refresh)
 */
export function clearImageCache(): void {
  const size = memoryCache.size;
  memoryCache.clear();
  pendingRequests.clear();
  requestCounts.clear();
  cacheMetadata.clear();
  // eslint-disable-next-line no-console
  console.log(`🗑️ V160.14: Cleared ${size} cached images`);
}

/**
 * Get cache statistics (for diagnostics)
 */
export function getCacheStats() {
  return {
    memorySize: memoryCache.size,
    pendingRequests: pendingRequests.size,
    totalRequests: Array.from(requestCounts.values()).reduce((a, b) => a + b, 0),
    deduplicatedRequests: Array.from(requestCounts.values()).filter(c => c > 1).length,
  };
}

// =============================================================================
// PRIVATE HELPERS
// =============================================================================

async function loadAndCacheImage(
  originalUrl: string,
  cacheKey: string,
  options: CloudinaryOptions
): Promise<string> {
  try {
    const optimizedUrl = getOptimizedImageUrl(originalUrl, {
      ...options,
      format: 'webp',
    });

    // V325.20: REMOVED preloadImage() — real <img> tags (V325.16) already
    // fetch and cache images via the browser. The hidden new Image() here
    // was duplicating every single request (proven by Network tab Initiator
    // column showing simpleImageCache.ts:223 alongside react-dom).
    // await preloadImage(optimizedUrl);

    memoryCache.set(cacheKey, optimizedUrl);
    storeCacheMetadata(cacheKey);

    return optimizedUrl;
  } catch (error) {
    console.error(`Photo cache error for ${cacheKey}:`, error);
    return originalUrl;
  }
}

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve();
    };
    img.onerror = (error) => {
      reject(new Error(`Failed to preload ${url}`));
    };
    img.src = url;
  });
}

function storeCacheMetadata(cacheKey: string): void {
  cacheMetadata.set(cacheKey, {
    timestamp: Date.now(),
    version: CACHE_VERSION,
  });
}

function isCacheValid(cacheKey: string): boolean {
  const metadata = cacheMetadata.get(cacheKey);
  if (!metadata) return false;
  if (metadata.version !== CACHE_VERSION) return false;
  const age = Date.now() - metadata.timestamp;
  if (age > CACHE_TTL_MS || age < 0) return false;
  return true;
}

export type { CloudinaryOptions };

